import api from "./api";

/**
 * Uploads a file directly to S3 via multipart presigned URLs when the backend
 * reports mode "multipart" (S3 configured). Returns the completion response,
 * or null when the backend is in proxy mode (caller should fall back to the
 * classic multipart/form-data endpoint). onProgress receives
 * { percent, loaded, total, speed (B/s), eta (s) }.
 */
export async function smartUpload(file, meta, onProgress, basePath = "/uploads") {
  const init = await api.post(`${basePath}/init`, {
    filename: file.name,
    size: file.size,
    content_type: file.type || "application/octet-stream",
    ...meta,
  });
  if (init.data.mode !== "multipart") return null;

  const { upload_id, key, part_size, urls } = init.data;
  const parts = [];
  let cursor = 0;
  let loaded = 0;
  const started = performance.now();
  const report = () => {
    if (!onProgress) return;
    const elapsed = (performance.now() - started) / 1000;
    const speed = elapsed > 0 ? loaded / elapsed : 0;
    onProgress({
      percent: Math.round((loaded / file.size) * 100),
      loaded,
      total: file.size,
      speed,
      eta: speed > 0 ? (file.size - loaded) / speed : null,
    });
  };
  const workers = Array.from({ length: Math.min(4, urls.length) }, async () => {
    while (cursor < urls.length) {
      const i = cursor++;
      const chunk = file.slice(i * part_size, (i + 1) * part_size);
      let res;
      for (let attempt = 0; attempt < 3; attempt++) {
        res = await fetch(urls[i], { method: "PUT", body: chunk });
        if (res.ok) break;
        if (attempt === 2) throw new Error(`Upload failed at part ${i + 1} (HTTP ${res.status})`);
      }
      parts.push({ ETag: res.headers.get("ETag"), PartNumber: i + 1 });
      loaded += chunk.size;
      report();
    }
  });
  try {
    await Promise.all(workers);
  } catch (e) {
    try {
      await api.post(`${basePath}/abort`, { upload_id, key });
    } catch (abortErr) {
      console.warn("multipart abort failed", abortErr);
    }
    throw e;
  }
  parts.sort((a, b) => a.PartNumber - b.PartNumber);
  const complete = await api.post(`${basePath}/complete`, { upload_id, key, parts });
  return complete.data;
}
